import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Star, Edit3 } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost, btnDanger } from "@/components/admin/ui";

const empty = { name: "", role: "", quote: "", rating: 5, approved: true };

export default function TestimonialsAdminPage() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const fetchItems = () => apiClient.get("/testimonials/admin").then((r) => setItems(r.data));
    useEffect(() => { fetchItems(); }, []);

    const toggle = async (t) => {
        await apiClient.patch(`/testimonials/${t.id}`, { approved: !t.approved });
        toast.success(t.approved ? "Unpublished" : "Approved & published");
        fetchItems();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this testimonial?")) return;
        await apiClient.delete(`/testimonials/${id}`);
        fetchItems();
    };

    return (
        <>
            <PageHeader
                title="Testimonials"
                subtitle="Approve customer reviews and curate the home page quotes."
                action={<button onClick={() => setEditing({ ...empty })} data-testid="add-testimonial-btn" className={btnPrimary}><Plus size={16} /> Add testimonial</button>}
            />

            <div className="grid md:grid-cols-2 gap-5">
                {items.map((t) => (
                    <Card key={t.id} className="p-5" data-testid={`testimonial-${t.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex gap-1">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={12} className="fill-walnut text-walnut" />)}</div>
                            {t.approved
                                ? <span className="text-[10px] uppercase tracking-widest bg-green-100 text-green-800 px-2 py-1">Published</span>
                                : <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-1">Pending</span>}
                        </div>
                        <p className="font-serif text-lg leading-snug">"{t.quote}"</p>
                        <div className="mt-3">
                            <div className="font-medium text-sm">{t.name}</div>
                            <div className="text-xs text-stone-muted">{t.role}</div>
                        </div>
                        <div className="flex gap-2 mt-4 pt-3 border-t border-stone-border dark:border-stone-800">
                            <button onClick={() => toggle(t)} data-testid={`approve-${t.id}`} className={btnGhost}>{t.approved ? <><X size={14} /> Unpublish</> : <><Check size={14} /> Approve</>}</button>
                            <button onClick={() => setEditing({ ...t })} className={btnGhost}><Edit3 size={14} /> Edit</button>
                            <button onClick={() => del(t.id)} data-testid={`delete-testimonial-${t.id}`} className={btnDanger}><Trash2 size={14} /></button>
                        </div>
                    </Card>
                ))}
                {!items.length && <div className="col-span-full text-stone-muted py-12">No testimonials yet.</div>}
            </div>

            {editing && <Modal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchItems(); }} />}
        </>
    );
}

function Modal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const isEdit = !!initial.id;
    const submit = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) await apiClient.patch(`/testimonials/${form.id}`, form);
            else await apiClient.post("/testimonials", form);
            onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-cream dark:bg-[#171311] w-full max-w-lg p-6 md:p-8" data-testid="testimonial-form">
                <div className="flex justify-between mb-6"><h2 className="font-serif text-2xl">{isEdit ? "Edit" : "New"} testimonial</h2><button type="button" onClick={onClose}><X /></button></div>
                <div className="space-y-4">
                    <Field label="Name" required><input data-testid="testimonial-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                    <Field label="Role / Project"><input className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></Field>
                    <Field label="Quote" required><textarea data-testid="testimonial-quote" required rows={4} className={inputCls + " resize-none"} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} /></Field>
                    <Field label="Rating">
                        <select className={inputCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })}>
                            {[1, 2, 3, 4, 5].map((r) => <option key={r} value={r}>{r} star{r > 1 && "s"}</option>)}
                        </select>
                    </Field>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.approved} onChange={(e) => setForm({ ...form, approved: e.target.checked })} className="accent-walnut" /> Publish on website
                    </label>
                </div>
                <div className="flex gap-3 mt-6">
                    <button className={btnPrimary} data-testid="testimonial-save">{isEdit ? "Save" : "Add"}</button>
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                </div>
            </form>
        </div>
    );
}
